import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import {
  ICreateAccountResponse,
  IOnboardingUrlResponse,
  IStripeDashboardLinkResponse,
  ICreatorProduct,
  ICreatorProductBase,
  ICreatorProductNormalizedResponse,
  ICreatorProductQueryParam,
  TCreatorProductRawResponse,
  TCreatorProductUpdate,
  IUserSubscription,
  TUserSubscriptionRawResponse,
  IUserSubscriptionQueryParam,
  IUserSubscriptionNormalizedResponse,
  ICreateDonationPayload,
  IDonation,
  IDonationQueryParam,
  IDonationRawPaginatedResponse,
  IDonationNormalizedPaginatedResponse,
  IPaymentTransaction,
  ITransactionNormalizedPaginatedResponse,
  ITransactionQueryParam,
  ITransactionRawPaginatedResponse,
  IUserSubscriptionActionPayload,
  IUserSubscriptionActionResponse,
  IUserSubscriptionsResponse,
  IPlatformBillingPlan,
  IPlatformSubscription,
  TPlatformSubscriptionRawResponse,
  IPlatformSubscriptionNormalizedResponse,
  IPlatformBillingPlanGroup,
  IStripePortalResponse,
  IValidTierDeletionSubscriptionQuery,
  IValidTierDeletionSubscriptionsResponse,
} from './typings';
import { QUERIES } from './constants';
import { createEntityAdapter } from '@reduxjs/toolkit';

export const creatorProductAdapter = createEntityAdapter({
  selectId: (item: ICreatorProduct) => item._id,
  // sortComparer: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
  // sortComparer: (a, b) => a.price - b.price,
});

export const creatorProductSelector = creatorProductAdapter.getSelectors();

export const userSubscriptionAdapter = createEntityAdapter({
  selectId: (item: IUserSubscription) => item._id,
  sortComparer: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
});

export const userSubscriptionSelector = userSubscriptionAdapter.getSelectors();

export const donationAdapter = createEntityAdapter({
  selectId: (item: IDonation) => item._id,
  sortComparer: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
});

export const donationSelector = donationAdapter.getSelectors();

export const transactionAdapter = createEntityAdapter({
  selectId: (item: IPaymentTransaction) => item._id,
  sortComparer: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
});

export const transactionSelector = transactionAdapter.getSelectors();

export const platformSubscriptionAdapter = createEntityAdapter({
  selectId: (item: IPlatformSubscription) => item._id,
  sortComparer: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
});

export const platformSubscriptionSelector = platformSubscriptionAdapter.getSelectors();

export const monetizationApiService = (baseQuery: BaseQueryFn) => {
  const api = createApi({
    reducerPath: 'monetization-api',
    baseQuery,
    tagTypes: [
      QUERIES.monetizationOnboarding,
      QUERIES.monetizationCreatorProducts,
      QUERIES.monetizationUserSubscriptions,
      QUERIES.monetizationDonations,
      QUERIES.monetizationTransactions,
      QUERIES.monetizationPlatformProducts,
    ],
    endpoints: (builder) => ({
      createStripeConnectAccount: builder.mutation<ICreateAccountResponse, { email: string }>({
        query: (accountData) => ({
          url: 'billing/v1/create',
          method: 'POST',
          body: accountData,
        }),
        invalidatesTags: [QUERIES.monetizationOnboarding],
      }),
      getOnboardingUrl: builder.query<IOnboardingUrlResponse, { accountId: string }>({
        query: ({ accountId }) => ({
          url: `billing/v1/onboarding/${accountId}`,
          method: 'GET',
        }),
        providesTags: (result, error, { accountId }) => [
          { type: QUERIES.monetizationOnboarding, id: accountId },
        ],
      }),
      getStripeDashboardLink: builder.query<IStripeDashboardLinkResponse, { email: string }>({
        query: ({ email }) => ({
          url: 'billing/v1/stripe-dashboard',
          method: 'GET',
          params: { email },
        }),
        providesTags: (result, error, { email }) => [
          { type: QUERIES.monetizationOnboarding, id: email },
        ],
      }),
      createProductTier: builder.mutation<ICreatorProduct, ICreatorProductBase>({
        query: (productData) => ({
          url: `billing/v1/products/${productData.creator_id}/create`,
          method: 'POST',
          body: productData,
        }),
        invalidatesTags: [QUERIES.monetizationCreatorProducts],
      }),
      getCreatorProducts: builder.query<
        ICreatorProductNormalizedResponse,
        ICreatorProductQueryParam
      >({
        query: ({ creator_id, product_ids, ...rest }) => ({
          url: `billing/v1/products/${creator_id}`,
          method: 'GET',
          params: { ...rest, product_ids: product_ids?.join(',') },
        }),
        providesTags: [QUERIES.monetizationCreatorProducts],
        transformResponse: (response: TCreatorProductRawResponse) => ({
          products: creatorProductAdapter.setAll(
            creatorProductAdapter.getInitialState(),
            response.products,
          ),
          total_pages: response.total_pages,
          total_records: response.total_records,
        }),
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSamePage = currentArg?.page === previousArg?.page;
          const isSameCreator = currentArg?.creator_id === previousArg?.creator_id;
          return !isSamePage || !isSameCreator;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs?.creator_id}-${queryArgs?.page}-${queryArgs?.product_ids}`,
        merge: (currentState, incomingState) => {
          creatorProductAdapter.setAll(
            currentState.products,
            creatorProductSelector.selectAll(incomingState.products),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
        },
      }),
      getProductById: builder.query<ICreatorProduct, { creator_id: string; product_id: string }>({
        query: ({ creator_id, product_id }) => ({
          url: `billing/v1/products/${creator_id}/${product_id}`,
          method: 'GET',
        }),
        providesTags: (result, error, { product_id }) => [
          { type: QUERIES.monetizationCreatorProducts, id: product_id },
        ],
      }),
      updateProductTier: builder.mutation<ICreatorProduct, TCreatorProductUpdate>({
        query: ({ _id, creator_id, ...rest }) => ({
          url: `billing/v1/products/${creator_id}/${_id}/update`,
          method: 'PATCH',
          body: rest,
        }),
        invalidatesTags: [QUERIES.monetizationCreatorProducts],
      }),
      deleteProductTier: builder.mutation<
        { success: boolean },
        { creator_id: string; product_id: string }
      >({
        query: ({ creator_id, product_id }) => ({
          url: `billing/v1/products/${creator_id}/${product_id}/delete`,
          method: 'DELETE',
        }),
        invalidatesTags: [QUERIES.monetizationCreatorProducts],
      }),
      getUserSubscriptions: builder.query<
        IUserSubscriptionNormalizedResponse,
        IUserSubscriptionQueryParam
      >({
        query: ({ user_id, creator_id, product_ids, ...rest }) => ({
          url: `billing/v1/user_subscriptions/${user_id}/${creator_id}`,
          method: 'GET',
          params: { ...rest, product_ids: product_ids?.join(',') },
        }),
        providesTags: [QUERIES.monetizationUserSubscriptions],
        transformResponse: (response: TUserSubscriptionRawResponse) => ({
          user_subscriptions: userSubscriptionAdapter.setAll(
            userSubscriptionAdapter.getInitialState(),
            response.user_subscriptions,
          ),
          total_pages: response.total_pages,
          total_records: response.total_records,
        }),
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSamePage = currentArg?.page === previousArg?.page;
          const isSameCreator = currentArg?.creator_id === previousArg?.creator_id;
          const isSameUser = currentArg?.user_id === previousArg?.user_id;
          return !isSamePage || !isSameCreator || !isSameUser;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs?.user_id}-${queryArgs?.creator_id}-${queryArgs?.page}-${queryArgs?.product_ids}`,
        merge: (currentState, incomingState) => {
          userSubscriptionAdapter.setAll(
            currentState.user_subscriptions,
            userSubscriptionSelector.selectAll(incomingState.user_subscriptions),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
        },
      }),
      getUserSubscriptionById: builder.query<
        IUserSubscription,
        { user_id: string; user_subscription_id: string }
      >({
        query: ({ user_id, user_subscription_id }) => ({
          url: `billing/v1/user_subscriptions/user_subscription/${user_subscription_id}/${user_id}`,
          method: 'GET',
        }),
        providesTags: (result, error, { user_subscription_id }) => [
          { type: QUERIES.monetizationUserSubscriptions, id: user_subscription_id },
        ],
      }),
      createUserSubscription: builder.mutation<
        IUserSubscriptionActionResponse,
        IUserSubscriptionActionPayload
      >({
        query: ({ creator_product_id, user_id }) => ({
          url: `billing/v1/user_subscriptions/${creator_product_id}/${user_id}/create`,
          method: 'POST',
        }),
        invalidatesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      upgradeUserSubscription: builder.mutation<
        IUserSubscriptionActionResponse,
        IUserSubscriptionActionPayload
      >({
        query: ({ creator_product_id, user_id }) => ({
          url: `billing/v1/user_subscriptions/${creator_product_id}/${user_id}/update`,
          method: 'POST',
        }),
        invalidatesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      downgradeUserSubscription: builder.mutation<
        IUserSubscriptionActionResponse,
        IUserSubscriptionActionPayload
      >({
        query: ({ creator_product_id, user_id }) => ({
          url: `billing/v1/user_subscriptions/${creator_product_id}/${user_id}/update`,
          method: 'POST',
        }),
        invalidatesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      cancelUserSubscription: builder.mutation<
        IUserSubscription,
        { user_subscription_id: string; user_id: string }
      >({
        query: ({ user_subscription_id, user_id }) => ({
          url: `billing/v1/user_subscriptions/${user_subscription_id}/${user_id}/cancel`,
          method: 'DELETE',
        }),
        invalidatesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      resumeUserSubscription: builder.mutation<
        string,
        { user_subscription_id: string; user_id: string }
      >({
        query: ({ user_subscription_id, user_id }) => ({
          url: `billing/v1/user_subscriptions/${user_subscription_id}/${user_id}/resume_payment`,
          method: 'POST',
        }),
        invalidatesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      createDonation: builder.mutation<
        {
          donation: IDonation;
          client_secret: string;
          creator_stripe_account_id: string;
          platform_flat_fee: number;
        },
        ICreateDonationPayload
      >({
        query: (donationData) => ({
          url: `billing/v1/donations/${donationData.creator_id}/${donationData.user_id}`,
          method: 'POST',
          body: { amount: donationData.amount },
        }),
        invalidatesTags: [QUERIES.monetizationDonations],
      }),
      getSentDonations: builder.query<IDonationNormalizedPaginatedResponse, IDonationQueryParam>({
        query: ({ user_id, page, pageSize }) => ({
          url: `billing/v1/donations/sent/${user_id}`,
          method: 'GET',
          params: { page, page_size: pageSize },
        }),
        providesTags: [QUERIES.monetizationDonations],
        transformResponse: (response: IDonationRawPaginatedResponse) => ({
          donations: donationAdapter.setAll(donationAdapter.getInitialState(), response.donations),
          page: response.page,
          page_size: response.page_size,
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs.user_id}-${queryArgs.page}`,
        merge: (currentState, incomingState) => {
          donationAdapter.setAll(
            currentState.donations,
            donationSelector.selectAll(incomingState.donations),
          );
          currentState.page = incomingState.page;
          currentState.page_size = incomingState.page_size;
        },
      }),
      getFanSubscriptions: builder.query<IUserSubscriptionsResponse, { fan_id: string }>({
        query: ({ fan_id }) => ({
          url: `billing/v1/user_subscriptions_data/${fan_id}`,
          method: 'GET',
        }),
        providesTags: [QUERIES.monetizationDonations],
      }),
      getValidSubscriptionsAfterTierDeletion: builder.query<
        IValidTierDeletionSubscriptionsResponse,
        IValidTierDeletionSubscriptionQuery
      >({
        query: ({ user_id, creator_id, product_ids }) => ({
          url: `billing/v1/user_subscriptions/valid_after_tier_deletion/${user_id}/${creator_id}`,
          method: 'GET',
          params: { product_ids: product_ids?.join(',') },
        }),
        providesTags: [QUERIES.monetizationUserSubscriptions],
      }),
      getReceivedDonations: builder.query<
        IDonationNormalizedPaginatedResponse,
        IDonationQueryParam
      >({
        query: ({ creator_id, page, pageSize }) => ({
          url: `billing/v1/donations/received/${creator_id}`,
          method: 'GET',
          params: { page, page_size: pageSize },
        }),
        providesTags: [QUERIES.monetizationDonations],
        transformResponse: (response: IDonationRawPaginatedResponse) => ({
          donations: donationAdapter.setAll(donationAdapter.getInitialState(), response.donations),
          page: response.page,
          page_size: response.page_size,
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs.creator_id}-${queryArgs.page}`,
        merge: (currentState, incomingState) => {
          donationAdapter.setAll(
            currentState.donations,
            donationSelector.selectAll(incomingState.donations),
          );
          currentState.page = incomingState.page;
          currentState.page_size = incomingState.page_size;
        },
      }),
      getTransactions: builder.query<
        ITransactionNormalizedPaginatedResponse,
        ITransactionQueryParam
      >({
        query: ({ user_id, transaction_type, status, page, pageSize }) => ({
          url: `billing/v1/transactions/${user_id}`,
          method: 'GET',
          params: { transaction_type, status, page, page_size: pageSize },
        }),
        providesTags: [QUERIES.monetizationTransactions],
        transformResponse: (response: ITransactionRawPaginatedResponse) => ({
          transactions: transactionAdapter.setAll(
            transactionAdapter.getInitialState(),
            response.transactions,
          ),
          page: response.page,
          page_size: response.page_size,
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs.user_id}-${queryArgs.transaction_type}-${queryArgs.status}-${queryArgs.page}`,
        merge: (currentState, incomingState) => {
          transactionAdapter.setAll(
            currentState.transactions,
            transactionSelector.selectAll(incomingState.transactions),
          );
          currentState.page = incomingState.page;
          currentState.page_size = incomingState.page_size;
        },
      }),
      getPlatformProducts: builder.query<IPlatformBillingPlan[], { user_id?: string } | void>({
        query: (args) => {
          const params: Record<string, any> = {};
          if (args && args.user_id) {
            params.user_id = args.user_id;
          }
          return {
            url: 'billing/v1/platform_products',
            method: 'GET',
            params,
          };
        },
        transformResponse: (response: { platform_products: IPlatformBillingPlan[] }) =>
          response.platform_products,
        providesTags: [QUERIES.monetizationPlatformProducts],
      }),
      getPlatformProductById: builder.query<IPlatformBillingPlan, { id: string }>({
        query: ({ id }) => ({
          url: `billing/v1/platform_products/${id}`,
          method: 'GET',
        }),
      }),
      getPlatformProductByCode: builder.query<IPlatformBillingPlan[], { code: string }>({
        query: ({ code }) => ({
          url: `billing/v1/platform_products/verify/${code}`,
          method: 'GET',
        }),
        transformResponse: (response: { platform_products: IPlatformBillingPlan[] }) =>
          response.platform_products, // <-- unwrap it here
      }),
      getPlatformSubscriptions: builder.query<
        IPlatformSubscriptionNormalizedResponse,
        { user_id: string; page: number; pageSize: number }
      >({
        query: ({ user_id, page, pageSize }) => ({
          url: 'billing/v1/platform_subscriptions',
          method: 'GET',
          params: { user_id, page, page_size: pageSize },
        }),
        transformResponse: (response: TPlatformSubscriptionRawResponse) => ({
          platform_subscriptions: platformSubscriptionAdapter.setAll(
            platformSubscriptionAdapter.getInitialState(),
            response.platform_subscriptions,
          ),
          total_pages: response.total_pages,
          total_records: response.total_records,
        }),
        providesTags: [QUERIES.monetizationPlatformProducts],
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs.user_id}-${queryArgs.page}`,
        merge: (currentState, incomingState) => {
          platformSubscriptionAdapter.setAll(
            currentState.platform_subscriptions,
            platformSubscriptionSelector.selectAll(incomingState.platform_subscriptions),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
        },
      }),
      getPlatformSubscriptionById: builder.query<
        IPlatformSubscription,
        { user_id: string; subscription_id: string }
      >({
        query: ({ user_id, subscription_id }) => ({
          url: `billing/v1/platform_subscriptions/${subscription_id}`,
          method: 'GET',
          params: { user_id },
        }),
      }),
      createPlatformSubscription: builder.mutation<
        IUserSubscriptionActionResponse,
        { platform_product_id: string; user_id: string }
      >({
        query: ({ platform_product_id, user_id }) => ({
          url: 'billing/v1/platform_subscriptions',
          method: 'POST',
          body: { platform_product_id, user_id },
        }),
      }),
      updatePlatformSubscription: builder.mutation<
        IUserSubscriptionActionResponse,
        { platform_product_id: string; user_id: string }
      >({
        query: ({ platform_product_id, user_id }) => ({
          url: `billing/v1/platform_subscriptions/update/${platform_product_id}/${user_id}`,
          method: 'POST',
          body: { platform_product_id, user_id },
        }),

        invalidatesTags: [QUERIES.monetizationPlatformProducts],
      }),
      cancelPlatformSubscription: builder.mutation<
        IPlatformSubscription,
        { subscription_id: string; user_id: string }
      >({
        query: ({ subscription_id, user_id }) => ({
          url: `billing/v1/platform_subscriptions/${subscription_id}/cancel`,
          method: 'DELETE',
          params: { user_id },
        }),
      }),
      resumePlatformSubscription: builder.mutation<
        string,
        { subscription_id: string; user_id: string }
      >({
        query: ({ subscription_id, user_id }) => ({
          url: `billing/v1/platform_subscriptions/${subscription_id}/resume`,
          method: 'POST',
          params: { user_id },
        }),
      }),
      getPlatformBillingPlanGroups: builder.query<IPlatformBillingPlanGroup[], void>({
        query: () => ({
          url: 'billing/v1/platform_billing_plan_groups',
          method: 'GET',
        }),
        transformResponse: (response: {
          platform_billing_plan_groups: IPlatformBillingPlanGroup[];
        }) => response.platform_billing_plan_groups,
      }),
      getPlatformBillingPlanGroupById: builder.query<IPlatformBillingPlanGroup, { id: string }>({
        query: ({ id }) => ({
          url: `billing/v1/platform_billing_plan_groups/${id}`,
          method: 'GET',
        }),
      }),
      createPlatformBillingPlanGroup: builder.mutation<
        IPlatformBillingPlanGroup,
        Omit<IPlatformBillingPlanGroup, '_id' | 'createdAt' | 'updatedAt'>
      >({
        query: (groupData) => ({
          url: 'billing/v1/platform_billing_plan_groups',
          method: 'POST',
          body: groupData,
        }),
      }),
      updatePlatformBillingPlanGroup: builder.mutation<
        IPlatformBillingPlanGroup,
        Partial<IPlatformBillingPlanGroup> & { id: string }
      >({
        query: ({ id, ...groupData }) => ({
          url: `billing/v1/platform_billing_plan_groups/${id}`,
          method: 'PUT',
          body: groupData,
        }),
      }),
      deletePlatformBillingPlanGroup: builder.mutation<void, { id: string }>({
        query: ({ id }) => ({
          url: `billing/v1/platform_billing_plan_groups/${id}`,
          method: 'DELETE',
        }),
      }),
      getStripeCustomerPortal: builder.mutation<IStripePortalResponse, { user_id: string }>({
        query: ({ user_id }) => ({
          url: `billing/v1/users/${user_id}/subscriptions/stripe-portal`,
          method: 'GET',
        }),
      }),
      createCheckoutSession: builder.mutation<
        { url: string },
        { user_id: string; platform_product_id: string }
      >({
        query: ({ user_id, platform_product_id }) => ({
          url: `billing/v1/users/${user_id}/subscriptions/checkout-session`,
          method: 'POST',
          body: { platform_product_id },
        }),
      }),
    }),
  });
  return api;
};
